import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, withRouter, Redirect } from 'react-router-dom'
import axios from 'axios'

import { routes } from '../../../constantes/routes'
import PlusBtn from '../../buttons/PlusBtn'
import FinalProductsTable from './FinalProductsTable'
import FolderProductsTable from './FolderProductsTable'
import Header from '../../header/Header'
import Spinner from '../../common/Spinner'
import { articleSearchApi } from '../../../api/articleSearchApi'
import {
  swalAlertIsConfirm,
  swalAlertError,
  swalSuccessGeneral,
} from '../../../utils/swalAlert'
import { swalTitle } from '../../../constantes/swalTitle'
import { swalMssgs } from '../../../constantes/swalMssgs'
import { productsActions, familiesActions } from '../../../actions/articlesActions'
import { familiesTypes, productsTypes } from '../../../constantes/actionTypes'
import { downloadCSV } from '../../../utils/downloadCSV'
import { exportActions } from '../../../actions/exportActions'
import { entitiesExport } from '../../../constantes/exportEndpoints'
import '../../../assests/styles/backoffice/articlesContainer.scss'

/**
 * Gestión de productos con el motor de búsqueda v2.
 *
 * Reemplazo de ProductsContainer, que se queda intacto. Reutiliza sus mismas
 * tablas de presentación (FinalProductsTable y FolderProductsTable) para que
 * el aspecto sea idéntico y no haya que mantener dos copias.
 *
 * Mantiene la carga por scroll, que es como funciona hoy la pantalla, pero
 * corrigiendo lo que allí falta:
 *
 *  · Freno real. El servidor devuelve hasMore, así que al llegar al final se
 *    deja de pedir. Hoy el scroll sigue incrementando la página para siempre.
 *  · Sin respuestas obsoletas. Cada búsqueda cancela la anterior; hoy una
 *    petición en vuelo puede inyectar resultados de la búsqueda previa.
 *  · El listener se desregistra de verdad (se quita la MISMA referencia
 *    throttled que se registró).
 *  · El indicador de carga se activa. Hoy existe la variable pero nunca se
 *    pone a true, así que durante la búsqueda se ve "no hay nada que mostrar".
 */

const TAM_PAGINA = 20

/** Cuánto antes del final se empieza a cargar la página siguiente. */
const MARGEN_SCROLL = 300

/** El DTO de v2 al que esperan las tablas del BackOffice. */
const aFilaDeTabla = (item) => {
  // Por el campo, no por la posición: el servidor devuelve la principal
  // primera, pero si su categoría estuviera inactiva quedaría fuera del join
  // y categorias[0] sería una secundaria, sin aviso.
  const principal = item.categorias.find((c) => c.esPrincipal)
  const otras = item.categorias.filter((c) => !c.esPrincipal)

  return {
    id_Articulo: item.id_Articulo,
    referencia: item.referencia,
    nombre: item.nombre,
    nombreCategoria: principal ? principal.nombre : ' ',
    // Aquí NO se usa CategoriasAlternativas: ese componente pide sus datos al
    // servidor, y en un listado de 20 filas serían 20 peticiones. La respuesta
    // de búsqueda ya trae categorias[].
    categoriasAlternativas: otras.map((c) => c.nombre),
    nombrePadreProducto: item.rutaPadres.length
      ? item.rutaPadres[item.rutaPadres.length - 1].nombre
      : '',
    nombreFamiliaProducto: item.rutaPadres.length
      ? item.rutaPadres[item.rutaPadres.length - 1].nombre
      : principal
      ? principal.nombre
      : '',
    nombreSubFamiliaProductos: [],
    cover: item.cover,
    documents: item.documents,
    images: item.images,
    coverCount: item.coverCount,
    imageCount: item.imageCount,
    documentCount: item.documentCount,
  }
}

const ProductsContainerV2 = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const userProfile = useSelector((estado) => estado.userProfile)

  const [mostrarFinales, setMostrarFinales] = useState(true)
  const [busqueda, setBusqueda] = useState('')
  const [busquedaAplicada, setBusquedaAplicada] = useState('')
  const [pagina, setPagina] = useState(1)

  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  // Cuántos hay en total sin contar el término: el universo sobre el que se
  // busca. Lo calcula el servidor, porque el cliente sólo lo conocería si el
  // usuario llega a ver la lista sin filtrar, y no siempre pasa.
  const [totalGeneral, setTotalGeneral] = useState(null)
  const [hayMas, setHayMas] = useState(false)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)
  const [ms, setMs] = useState(null)

  // El listener de scroll no puede leer el estado directamente: se registra
  // una sola vez y vería siempre los valores del primer render.
  // Qué página se está pidiendo ahora mismo (0 = ninguna). Sirve para
  // distinguir la primera carga de las siguientes: "Cargando más…" sólo tiene
  // sentido a partir de la segunda.
  const [paginaCargando, setPaginaCargando] = useState(0)

  const cargandoRef = useRef(false)
  const hayMasRef = useRef(false)
  const centinelaRef = useRef(null)
  const peticionRef = useRef(0)

  const scope = mostrarFinales ? 'final' : 'folder'

  // Debounce del texto tecleado.
  useEffect(() => {
    const t = setTimeout(() => setBusquedaAplicada(busqueda.trim()), 350)
    return () => clearTimeout(t)
  }, [busqueda])

  // Al cambiar de pestaña o de término se empieza de cero.
  useEffect(() => {
    setPagina(1)
    setItems([])
    setTotal(0)
    // Hasta que responda la primera página no se sabe si hay más. Sin esto,
    // el centinela podría pedir la página 2 usando el hasMore de la búsqueda
    // anterior.
    setHayMas(false)
    hayMasRef.current = false
  }, [busquedaAplicada, scope])

  /**
   * Carga de la página actual.
   *
   * Cada petición lleva un identificador y sólo la última en curso puede
   * tocar el estado. Con eso desaparece toda la familia de carreras: una
   * respuesta que llega tarde no pisa a otra más reciente, y una petición
   * cancelada no deja el indicador de carga encendido.
   *
   * La limpieza invalida el identificador antes de cancelar, así que al
   * desmontar no queda ningún setState pendiente.
   */
  useEffect(() => {
    const fuente = axios.CancelToken.source()
    const idPeticion = peticionRef.current + 1
    peticionRef.current = idPeticion

    const paginaPedida = pagina
    const t0 = performance.now()

    setCargando(true)
    cargandoRef.current = true
    setPaginaCargando(paginaPedida)
    setError(null)

    const terminar = () => {
      setCargando(false)
      cargandoRef.current = false
      setPaginaCargando(0)
    }

    articleSearchApi
      .search(
        { q: busquedaAplicada, scope, page: paginaPedida, pageSize: TAM_PAGINA },
        fuente.token,
      )
      .then((res) => {
        if (idPeticion !== peticionRef.current) return

        const datos = res.data
        const filas = (datos.items || []).map(aFilaDeTabla)

        setItems((previos) =>
          paginaPedida === 1 ? filas : previos.concat(filas),
        )
        setTotal(datos.total)
        setTotalGeneral(datos.totalSinFiltro)
        setHayMas(datos.hasMore)
        hayMasRef.current = datos.hasMore
        setMs(Math.round(performance.now() - t0))
        terminar()
      })
      .catch((err) => {
        if (idPeticion !== peticionRef.current) return

        if (!axios.isCancel(err)) {
          const codigo = err && err.response ? err.response.status : '—'
          setError(`No se ha podido buscar (HTTP ${codigo}).`)
          setHayMas(false)
          hayMasRef.current = false
        }
        terminar()
      })

    return () => {
      peticionRef.current += 1
      fuente.cancel()
    }
  }, [busquedaAplicada, scope, pagina])

  /**
   * Carga por scroll mediante un centinela al final de la lista.
   *
   * No se usa un listener de scroll sobre window, que es como funciona la
   * pantalla actual, porque mide document.body.scrollHeight: el fondo de TODA
   * la página. En modo comparación hay dos listas apiladas, así que recorrer
   * la de arriba nunca te acerca al fondo del documento y no se dispara nada;
   * y al llegar abajo se dispararían las dos a la vez.
   *
   * El centinela se activa cuando el final de ESTA lista entra en pantalla,
   * sin importar qué elemento haga scroll ni cuántas listas haya.
   */
  useEffect(() => {
    const centinela = centinelaRef.current
    if (!centinela || typeof IntersectionObserver === 'undefined') return undefined

    const observador = new IntersectionObserver(
      (entradas) => {
        const visible = entradas.some((e) => e.isIntersecting)
        if (visible && !cargandoRef.current && hayMasRef.current) {
          setPagina((p) => p + 1)
        }
      },
      { rootMargin: `0px 0px ${MARGEN_SCROLL}px 0px` },
    )

    observador.observe(centinela)

    return () => observador.disconnect()
  }, [])

  const quitar = useCallback(
    async (idArticulo) => {
      try {
        const alerta = await swalAlertIsConfirm(
          '¿Seguro que quieres eliminar el producto?',
        )
        if (!alerta.isConfirmed) return

        const respuesta = mostrarFinales
          ? await dispatch(productsActions.deleteProduct(idArticulo))
          : await dispatch(familiesActions.deleteFamily(idArticulo))

        if (
          respuesta.type === productsTypes.DELETE_PRODUCT_SUCCESS ||
          respuesta.type === familiesTypes.DELETE_FAMILY_SUCCESS
        ) {
          setItems((previos) =>
            previos.filter((x) => x.id_Articulo !== idArticulo),
          )
          setTotal((t) => (t > 0 ? t - 1 : 0))
          await swalSuccessGeneral(swalTitle.Eliminado, swalMssgs.DELETE_SUCCESS)
        }
      } catch (err) {
        try {
          swalAlertError(err.response.data || err.response.statusText)
        } catch (e) {
          // El motor antiguo hace lo mismo: si no hay cuerpo, no hay aviso.
        }
      }
    },
    [mostrarFinales, dispatch],
  )

  const exportarCSV = async () => {
    const respuesta = await dispatch(exportActions.exportCSV(entitiesExport.ARTICLES))
    downloadCSV('Articulos', respuesta)
  }

  if (!userProfile.roles.isBackofficeVer) {
    return <Redirect to={{ pathname: routes.MAIN }} />
  }

  return (
    <div className='products'>
      <Header
        text={'Gestionar productos'}
        route={() => history.push(routes.BACKOFFICE)}
        backBtn={true}
      />

      <div className='filter__wrap mb-80'>
        <div className='search' style={{ minWidth: '120px' }}>
          <div className='searchbar__wrap'>
            <h4 className='filter__title'>Buscador</h4>
            <input
              className='btn-filter search placeholder-text'
              type='text'
              value={busqueda}
              placeholder='Buscar...'
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
        </div>

        <div className='add__action__rigth__side'>
          <div className='categories-products-btn'>
            <div>
              <button
                className='user-input btn-shape'
                onClick={() => setMostrarFinales(true)}
              >
                Productos
              </button>
            </div>
            <div>
              <button
                className='user-input btn-shape'
                onClick={() => setMostrarFinales(false)}
              >
                famílias/subfamílias
              </button>
            </div>
          </div>
          <button
            onClick={exportarCSV}
            className='btn-landing'
            style={{ cursor: 'pointer', marginRight: '10px' }}
          >
            📊 Exportar CSV
          </button>
          <button className='action-button' onClick={() => history.push(routes.PRODUCTS_NEW)}>
            <PlusBtn />
            <span>Añadir productos</span>
          </button>
        </div>
      </div>

      {/* El recuento vive en la cabecera de la tabla, donde ya había un
          "N resultados.". No hace falta una fila aparte.
          El tiempo de respuesta se mide pero no se muestra: era útil para
          comparar motores, no para el usuario. Para verlo en pantalla,
          descomentar en la cabecera de la tabla o aquí:
          {ms !== null ? <span>{ms} ms</span> : null} */}

      {error ? (
        <div
          style={{
            margin: '0 0 14px',
            padding: '8px 12px',
            fontSize: 13.5,
            fontWeight: 600,
            color: '#a32218',
            background: '#f6e3e1',
            borderLeft: '3px solid #a32218',
          }}
        >
          {error}
        </div>
      ) : null}

      {mostrarFinales ? (
        <FinalProductsTable
          finalProducts={items}
          toEditArticle={(id) => history.push(routes.PRODUCTS_EDIT + id)}
          handleRemove={quitar}
          userProfile={userProfile}
          loading={cargando}
          total={total}
          totalGeneral={totalGeneral}
          filtrando={busquedaAplicada !== ''}
        />
      ) : (
        <FolderProductsTable
          foldersProducts={items}
          toEditArticle={(id) => history.push(routes.PRODUCTS_EDIT + id)}
          handleRemove={quitar}
          userProfile={userProfile}
          loading={cargando}
          total={total}
          totalGeneral={totalGeneral}
          filtrando={busquedaAplicada !== ''}
        />
      )}

      {/* Centinela: al entrar en pantalla pide la siguiente página. */}
      <div ref={centinelaRef} style={{ height: 1 }} />

      <div style={{ textAlign: 'center', padding: '16px 0' }}>
        {paginaCargando > 1 ? <Spinner texto='Cargando más...' /> : null}

        {/* Alternativa explícita al centinela: si el navegador no soporta
            IntersectionObserver, o si la lista cabe entera en pantalla y por
            tanto nunca hay scroll, esto sigue permitiendo avanzar. */}
        {!cargando && hayMas ? (
          <button
            className='user-input btn-shape'
            onClick={() => setPagina((p) => p + 1)}
          >
            Cargar más ({total - items.length} restantes)
          </button>
        ) : null}

        {!cargando && !hayMas && items.length > 0 ? (
          <span style={{ color: '#8b97a3', fontSize: 13 }}>
            No hay más resultados.
          </span>
        ) : null}
      </div>
    </div>
  )
}

export default withRouter(ProductsContainerV2)
